import { ref, onMounted, onUnmounted } from 'vue'
import { useGoogleMaps } from './useGoogleMaps.js'

export function useGooglePlacesAutocomplete(inputElementId, apiKey = 'AIzaSyABZyTkuDW5p7pO6cCGvmVrSU40s77Rs5A') {
  const selectedLocation = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  
  let autocomplete = null
  let googleMaps = null
  let processingSelection = false // Flag to prevent rapid-fire selections

  const initializeAutocomplete = async () => {
    try {
      isLoading.value = true
      error.value = null

      // Load Google Maps with Places library
      googleMaps = await useGoogleMaps(apiKey)
      
      // Double check that Google Maps and Places are available
      if (!googleMaps || !googleMaps.places || !googleMaps.places.Autocomplete) {
        throw new Error('Google Maps Places library not properly loaded')
      }
      
      // Get the input element
      const inputElement = document.getElementById(inputElementId)
      if (!inputElement) {
        throw new Error(`Input element with id "${inputElementId}" not found`)
      }

      // Create autocomplete instance with additional error handling
      try {
        autocomplete = new googleMaps.places.Autocomplete(inputElement, {
          types: ['geocode'], // Only geographic locations
          fields: ['geometry.location', 'formatted_address', 'name']
        })
      } catch (autocompleteError) {
        console.error('Error creating autocomplete instance:', autocompleteError)
        throw new Error('Failed to initialize autocomplete: ' + autocompleteError.message)
      }

      // Listen for place selection with maximum isolation from Vue's reactivity
      const handlePlaceChanged = () => {
        // Prevent rapid-fire selections
        if (processingSelection) {

          return;
        }
        
        processingSelection = true;
        
        // Use requestAnimationFrame and setTimeout to completely break out of Google's callback stack
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              // Get place data completely outside of Vue's reactive context
              const place = autocomplete.getPlace();
              
              if (!place) {
                console.warn('No place data received');
                error.value = 'No place data received';
                return;
              }
              
              // Check for geometry data
              if (!place.geometry || !place.geometry.location) {
                console.warn('No geometry/location data in place:', place);
                error.value = 'No location data found for selected place';
                return;
              }
              
              // Extract coordinates using a completely isolated approach
              const location = place.geometry.location;
              let lat, lng, address;
              
              try {
                // Handle different Google Maps API versions and formats
                if (typeof location.lat === 'function') {
                  lat = location.lat();
                } else if (typeof location.lat === 'number') {
                  lat = location.lat;
                } else if (location.lat && typeof location.lat.valueOf === 'function') {
                  lat = location.lat.valueOf();
                } else {
                  throw new Error('Unable to extract latitude');
                }
                
                if (typeof location.lng === 'function') {
                  lng = location.lng();
                } else if (typeof location.lng === 'number') {
                  lng = location.lng;
                } else if (location.lng && typeof location.lng.valueOf === 'function') {
                  lng = location.lng.valueOf();
                } else {
                  throw new Error('Unable to extract longitude');
                }
                
                // Extract address safely
                address = '';
                if (place.formatted_address && typeof place.formatted_address === 'string') {
                  address = place.formatted_address;
                } else if (place.name && typeof place.name === 'string') {
                  address = place.name;
                }
                
              } catch (coordError) {
                console.error('Error extracting coordinates:', coordError);
                error.value = 'Error extracting location coordinates';
                return;
              }
              
              // Validate coordinates
              const numLat = parseFloat(lat);
              const numLng = parseFloat(lng);
              
              if (isNaN(numLat) || isNaN(numLng) || numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) {
                console.error('Invalid coordinates:', { lat: numLat, lng: numLng });
                error.value = 'Invalid location coordinates';
                return;
              }
              
              // Create a completely clean, serializable object
              const locationData = JSON.parse(JSON.stringify({
                lat: numLat,
                lng: numLng,
                formatted_address: String(address)
              }));
              

              
              // Use another setTimeout to ensure we're completely outside of any callback context
              setTimeout(() => {
                try {
                  selectedLocation.value = locationData;

                } catch (setError) {
                  console.error('Error setting location:', setError);
                  error.value = 'Error setting location data';
                }
              }, 0);
              
            } catch (err) {
              console.error('Error in place selection handler:', err);
              error.value = 'Error processing selected location: ' + (err.message || 'Unknown error');
            } finally {
              // Reset processing flag after a longer delay
              setTimeout(() => {
                processingSelection = false;
              }, 250);
            }
          }, 0);
        });
      };
      
      // Add the listener with the isolated handler
      autocomplete.addListener('place_changed', handlePlaceChanged);

      isLoading.value = false
    } catch (err) {
      console.error('Error initializing autocomplete:', err)
      error.value = err.message
      isLoading.value = false
    }
  }

  const clearSelection = () => {
    selectedLocation.value = null
    error.value = null
    
    // Clear the input field if autocomplete exists
    if (autocomplete) {
      const inputElement = document.getElementById(inputElementId)
      if (inputElement) {
        inputElement.value = ''
      }
    }
  }

  const destroy = () => {
    try {
      if (autocomplete && googleMaps && googleMaps.event) {
        googleMaps.event.clearInstanceListeners(autocomplete)
      }
    } catch (cleanupError) {
      console.warn('Error cleaning up autocomplete listeners:', cleanupError)
    } finally {
      autocomplete = null
    }
  }

  onMounted(() => {
    initializeAutocomplete()
  })

  onUnmounted(() => {
    destroy()
  })

  return {
    selectedLocation,
    isLoading,
    error,
    clearSelection,
    initializeAutocomplete
  }
}